import * as React from 'react';
import Box from '@mui/material/Box';
import { UserContext } from '../context/userContext';
import {
  AppBar,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { conversationService, libraryService, userService } from '../api';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { grey } from '@mui/material/colors';
import {
  BotInput,
  Confirm,
  ConversationQa,
  ListSkeleton,
  ShareConversation,
} from '../molecules';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useModal } from '../hooks';
import { gatBundleNameList } from '../tools';

interface Props {
  uid?: string | null;
}

export const Conversation: React.FC<Props> = ({ uid }) => {
  const { loggedIn } = React.useContext(UserContext);
  const listRef = useRef<HTMLDivElement>(null);
  const { retrievalBundlesTagsList = [] } = libraryService();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { openModal, closeModal, currentModal } =
    useModal<'deleteConversation'>();

  const { conversationId = uid } = useParams();
  const isInlineView = !!uid;
  const { usersList } = userService();

  if (!conversationId) {
    return null;
  }

  const {
    sendQuestion,
    conversationIsPending,
    questionIsPending,
    updateConversation,
    conversationDelete,
    getConversation,
  } = conversationService();

  const {
    qas: conversationQas,
    retrieval_bundle_slugs,
    name: questionAsked,
    owner_uid,
    is_owned: isOwner,
    description,
  } = getConversation(conversationId);

  const chips = gatBundleNameList(
    retrievalBundlesTagsList,
    retrieval_bundle_slugs,
  );
  const author = usersList?.find(({ kc_uid }) => owner_uid === kc_uid);
  const authorName = `${author?.first_name} ${author?.last_name} `;

  const [askedQuestion, setAskedQuestion] = React.useState('');
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [title, setTitle] = React.useState(questionAsked);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  // useEffect(() => {
  //   // Clear state
  //   setIsCollapsed(false);
  // }, [conversationId]);

  useEffect(() => {
    setTitle(questionAsked);
  }, [questionAsked]);

  // After question returned
  useEffect(() => {
    setTimeout(() => {
      listRef.current?.lastElementChild?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 100);
    setAskedQuestion('');
  }, [conversationQas]);

  const handleSubmitQuestion = (question: string) => {
    setAskedQuestion(question);
    listRef.current?.lastElementChild?.scrollIntoView({
      behavior: 'smooth',
    });

    sendQuestion({
      uid: conversationId,
      prompt: question,
    });
  };

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleDelete = () => {
    openModal('deleteConversation');
  };

  const deleteConversation = () => {
    conversationDelete
      .mutateAsync({
        uid: conversationId,
      })
      .catch(() => {})
      .finally(() => {
        navigate('/');
      });
  };

  const handleRenameTitle = () => {
    setEditingTitle(!editingTitle);
  };

  const handleSubmitTitle = () => {
    setEditingTitle(false);
    if (title) {
      updateConversation({
        uid: conversationId,
        name: title,
        description,
      });
    }
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const handleFollowUpQuestion = handleSubmitQuestion;

  const sortedAnswers = (conversationQas || []).sort(
    ({ created_at: cr1 }, { created_at: cr2 }) => {
      return cr1.localeCompare(cr2);
    },
  );

  return loggedIn ? (
    <>
      <Helmet>
        <title>{`Conversation: ${questionAsked}`}</title>
      </Helmet>

      <AppBar position="sticky">
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            // swi
            background: isOwner ? '#dfdfdf' : '#bedbf3',
          }}
        >
          {editingTitle && (
            <TextField
              sx={{
                background: 'white',
                marginRight: '16px',
              }}
              defaultValue={questionAsked}
              hiddenLabel
              fullWidth
              multiline
              size="small"
              value={title}
              onChange={handleTitleChange}
              id="title"
            />
          )}
          {!editingTitle && (
            <Stack spacing={1} direction="row" alignItems="center">
              <div>
                <Tooltip title={questionAsked}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'black',
                      maxWidth: '600px',
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1,
                    }}
                  >
                    {questionAsked}
                  </Typography>
                </Tooltip>
                {!isOwner && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'black',
                    }}
                  >
                    Shared by author: <strong>{authorName}</strong>
                  </Typography>
                )}
              </div>

              {!!chips?.length &&
                chips.map((chipLabel) => (
                  <Chip
                    key={chipLabel}
                    label={chipLabel}
                    size="small"
                    variant="outlined"
                    sx={{
                      background: 'white',
                    }}
                  />
                ))}
            </Stack>
          )}
          <Stack spacing={0} direction="row" alignItems="center">
            {isOwner && (
              <>
                <Box pr={1}>
                  {!editingTitle && (
                    <Button
                      size="small"
                      onClick={handleRenameTitle}
                      variant="contained"
                    >
                      Rename
                    </Button>
                  )}
                  {editingTitle && (
                    <Button
                      size="small"
                      onClick={handleSubmitTitle}
                      variant="contained"
                    >
                      Save
                    </Button>
                  )}
                </Box>

                <ShareConversation {...{ conversationId }} />
                <IconButton
                  sx={{
                    color: 'black',
                    margin: 0,
                  }}
                  onClick={handleDelete}
                  size="small"
                >
                  <DeleteForeverIcon />
                </IconButton>
              </>
            )}
            <IconButton
              sx={{
                color: 'black',
              }}
              onClick={handleToggle}
              size="small"
            >
              {isCollapsed ? (
                <KeyboardDoubleArrowDownIcon />
              ) : (
                <KeyboardDoubleArrowUpIcon />
              )}
            </IconButton>
          </Stack>
        </Toolbar>
        {/* <Toolbar />
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                // onClick={handleDrawerToggle}KeyboardDoubleArrowUpIcon
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <KeyboardDoubleArrowUpIcon />
              </IconButton>
            </Toolbar> */}
      </AppBar>
      <Box component="main" p={2} bgcolor={grey[300]}>
        {conversationIsPending && (
          <Paper>
            <Stack padding={2}>
              <ListSkeleton numOfItems={12} width="100%" />
            </Stack>
          </Paper>
        )}

        <Box position="sticky" bottom={16}></Box>
        {!conversationIsPending && (
          <Stack spacing={2}>
            <Stack spacing={2} ref={listRef}>
              {sortedAnswers?.map((data, i) => (
                <ConversationQa
                  data={data}
                  key={data.uid}
                  conversationId={conversationId}
                  onFollowUpQuestion={handleFollowUpQuestion}
                  number={i + 1}
                  collapsed={isCollapsed}
                ></ConversationQa>
              ))}
            </Stack>
            <BotInput
              value={askedQuestion}
              loading={questionIsPending}
              onSubmit={handleSubmitQuestion}
            />
          </Stack>
        )}
        <Confirm
          show={currentModal === 'deleteConversation'}
          title="Deleting Conversation"
          question="Are you sure you want to delete this Conversation?"
          confirmLabel="Delete"
          onClick={(confirm) => {
            if (confirm) {
              deleteConversation();
            }
            closeModal('deleteConversation');
          }}
        />
      </Box>
    </>
  ) : null;
};
